import { Label, SelectWrapper } from './Select.styles';
import Select, { GroupBase, StylesConfig } from 'react-select';
import { ErrorText, InputDetail } from 'components/Common/Common.styles';
import { ReactNode, useState } from 'react';
import type { Props } from 'react-select/base';
import { CustomDropdownIndicator } from './CustomDropdownIndicator';
import { CustomOption } from './CustomOption';
import { CustomMultiValueRemove } from './CustomMultiValueRemove';
import { CustomMultiValue } from './CustomMultiValue';
import clsx from 'clsx';
import CustomPlaceholder from './CustomPlaceholder';
import { useTheme } from 'styled-components';

export type OptionType = {
  value: string;
  label: string;
  subvalue?: string;
};

declare module 'react-select/base' {
  // TS marks the following generics as unused, but they're necessary to
  // augment the base Props properly
  export interface Props<
    Option,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    IsMulti extends boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Group extends GroupBase<Option>,
  > {
    label?: string;
    subtitle?: string;
    error?: string;
    info?: string;
    width?: number | string;
    className?: string;
    rightContent?: ReactNode;
  }
}

const InfoComponent = ({
  selectedValue,
}: {
  selectedValue: unknown;
}): ReactNode | null => {
  if (
    selectedValue &&
    selectedValue instanceof Object &&
    'infoComponent' in selectedValue
  ) {
    return selectedValue.infoComponent as ReactNode;
  }
  return null;
};

// Disabling ref forwarding as it breaks types,
// See https://github.com/JedWatson/react-select/discussions/5141 for some leads
// on how to get it back

export const GraniteSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  label,
  error,
  width,
  info,
  subtitle,
  className,
  isClearable = false,
  classNames,
  components,
  rightContent,
  overrideStyles,
  variant = 'default',
  ...props
}: Partial<Props<Option, IsMulti, Group>> & {
  overrideStyles?: StylesConfig<Option, IsMulti, Group>;
  variant?: 'default' | 'blue' | 'surface-2' | 'ia';
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState('');

  const scrollbarStyle = {
    scrollbar: {
      width: '10px',
    },
    '&::-webkit-scrollbar': {
      width: '5px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'var(--stroke-base-subdued)',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'var(--background-base-surface-1)',
    },
  };

  const defaultStyles: StylesConfig<Option, IsMulti, Group> = {
    menu: (provided, state) => {
      return {
        ...provided,
        width: width || '100%',
        marginTop: 0,
        padding: 0,
        borderRadius: state.placement === 'top' ? '4px 4px 0 0' : '0 0 4px 4px',
        background: 'rgb(var(--input-background-unfilled))',
        zIndex: 9999,
        color: 'rgb(var(--input-stroke-focus))',
        '&:after': {
          content: '""',
          position: 'absolute',
          top: state.placement === 'top' ? '100%' : '-4px',
          pointerEvents: 'none',
          left: '-1px',
          right: '-1px',
          height: '4px',
          border: '1px solid currentColor',
          ...(state.placement === 'top' && {
            borderBottom: 'none',
          }),
          ...(state.placement === 'bottom' && {
            borderTop: 'none',
          }),
          backgroundColor: 'inherit',
        },
      };
    },
    menuList: (provided) => ({
      ...provided,
      maxHeight: 200,
      overflowY: 'auto',
      overflowX: 'hidden',
      ...scrollbarStyle,
    }),
    control: (provided, state) => {
      const getBorderColor = () => {
        if (state.isFocused) {
          return 'rgb(var(--input-stroke-focus))';
        }
        return error
          ? 'rgb(var(--input-stroke-error))'
          : 'rgb(var(--input-stroke-filled))';
      };

      return {
        ...provided,
        height: 48,
        width: width || '100%',
        backgroundColor: 'rgb(var(--input-background-filled))',
        border: `1px solid ${getBorderColor()}`,
        fontSize: theme.text.size.lg,
        fontWeight: theme.fontWeights.medium,
        borderRadius: 4,
        padding: '0 15px',
        '&:hover': {
          borderColor: 'rgb(var(--input-stroke-hover))',
          backgroundColor: 'rgb(var(--input-background-hover))',
          borderWidth: '1px',
        },
        '&:active': {
          borderColor: 'rgb(var(--input-stroke-focus))',
          backgroundColor: 'rgb(var(--input-background-focus))',
        },
        '&:focus': {
          borderWidth: '2px',
          borderColor: 'rgb(var(--input-stroke-focus))',
          backgroundColor: 'rgb(var(--input-background-focus))',
          outline: 'none',
        },
        '&:disabled': {
          borderColor: 'rgb(var(--input-stroke-disabled))',
          backgroundColor: 'rgb(var(--input-background-disabled))',
        },
      };
    },
    singleValue: (provided, props) => ({
      ...provided,
      color: error
        ? 'rgb(var(--input-content-error))'
        : props.isDisabled
          ? 'rgb(var(--content-input-disabled))'
          : 'rgb(var(--content-base-default))',
    }),
    multiValue: (provided) => ({
      ...provided,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '32px',
      padding: '0px 8px 0px 12px',
      borderRadius: '32px',
      border: '1px solid rgba(var(--stroke-base-subdued))',
      backgroundColor: 'rgba(var(--stroke-base-subdued))',
      fontSize: '16px',
      color: 'rgb(var(--content-base-default))',
      maxWidth: 'calc(100% - 12px)',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      marginRight: '8px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      maxHeight: '40px',
      overflowY: 'auto',
      gap: '5px',
      ...scrollbarStyle,
    }),
    placeholder: (provided) => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '100%',
      maxWidth: '100%',
    }),
  };

  const blueVariantStyles: Partial<StylesConfig<Option, IsMulti, Group>> = {
    control: (provided, state) => {
      const getBorderColor = () => {
        if (state.isFocused) {
          return 'rgb(var(--accent-blue))';
        }
        return error
          ? 'rgb(var(--input-stroke-error))'
          : 'rgb(var(--input-stroke-filled))';
      };
      return {
        ...defaultStyles.control?.(provided, state),
        backgroundColor: 'rgb(var(--background-base-surface-0))',
        border: `1px solid ${getBorderColor()}`,
        '&:hover': {
          borderColor: 'rgb(var(--accent-blue))',
        },
        '&:active': {
          borderColor: 'rgb(var(--accent-blue))',
        },
        '&:focus': {
          borderColor: 'rgb(var(--accent-blue))',
        },
        '&:disabled': {
          borderColor: 'rgb(var(--input-stroke-disabled))',
          backgroundColor: 'rgb(var(--input-background-disabled))',
        },
      };
    },
    menu: (provided, props) => ({
      ...defaultStyles.menu?.(provided, props),
      backgroundColor: 'rgb(var(--background-base-surface-0))',
      opacity: 1,
      zIndex: 9999999,
      color: 'rgb(var(--accent-blue))',
    }),
    menuList: (provided, props) => ({
      ...defaultStyles.menuList?.(provided, props),
      backgroundColor: 'rgb(var(--background-base-surface-0))',
    }),
  };

  const IAStyles: Partial<StylesConfig<Option, IsMulti, Group>> = {
    control: (provided, state) => {
      return {
        ...blueVariantStyles.control?.(provided, state),
        borderRadius: 4,
        border: 'inherit',

        background: `linear-gradient(
          90deg,
          rgba(126, 119, 179, 0.08) 0.1%,
          rgba(137, 137, 255, 0.08) 25.07%,
          rgba(108, 157, 255, 0.08) 49.94%,
          rgba(120, 207, 218, 0.08) 75%,
          rgba(159, 244, 169, 0.08) 99.97%
    )`,
        backgroundColor: 'rgb(var(--background-base-surface-1))',
      };
    },
    menu: (provided) => {
      return {
        ...provided,
        zIndex: 9999,
        marginTop: 0,
        background: `linear-gradient(
          90deg,
          rgba(126, 119, 179, 0.08) 0.1%,
          rgba(137, 137, 255, 0.08) 25.07%,
          rgba(108, 157, 255, 0.08) 49.94%,
          rgba(120, 207, 218, 0.08) 75%,
          rgba(159, 244, 169, 0.08) 99.97%
    )`,
        backgroundColor: 'rgb(var(--background-base-surface-1))',
        '&:after': undefined,
        borderColor: 'transparent',
      };
    },
    menuList: (provided, props) => ({
      ...blueVariantStyles.menuList?.(provided, props),
      padding: 10,
      zIndex: 9999,
      background: `linear-gradient(
          90deg,
          rgba(126, 119, 179, 0.08) 0.1%,
          rgba(137, 137, 255, 0.08) 25.07%,
          rgba(108, 157, 255, 0.08) 49.94%,
          rgba(120, 207, 218, 0.08) 75%,
          rgba(159, 244, 169, 0.08) 99.97%
    )`,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: error
        ? 'rgb(var(--input-content-error))'
        : 'rgb(var(--input-content-filled))',
      fontWeight: 600,
    }),
  };

  const transparentVariantStyles: Partial<
    StylesConfig<Option, IsMulti, Group>
  > = {
    menu: (provided, state) => {
      return {
        ...provided,
        width: width || '100%',
        marginTop: 0,
        padding: 0,
        borderRadius: state.placement === 'top' ? '4px 4px 0 0' : '0 0 4px 4px',
        background: 'rgb(var(--_background-base-surface-2))',
        zIndex: 9999,
        color: 'rgb(var(--input-stroke-focus))',
        '&:after': {
          content: '""',
          position: 'absolute',
          top: state.placement === 'top' ? '100%' : '-4px',
          pointerEvents: 'none',
          left: '-1px',
          right: '-1px',
          height: '4px',
          border: '1px solid currentColor',
          ...(state.placement === 'top' && {
            borderBottom: 'none',
          }),
          ...(state.placement === 'bottom' && {
            borderTop: 'none',
          }),
          backgroundColor: 'inherit',
        },
      };
    },
    control: (provided, state) => {
      const getBorderColor = () => {
        if (state.isFocused) {
          return 'rgb(var(--input-stroke-focus))';
        }
        return error
          ? 'rgb(var(--input-stroke-error))'
          : 'rgb(var(--input-stroke-filled))';
      };

      return {
        ...provided,
        height: 48,
        width: width || '100%',
        backgroundColor: 'rgb(var(--_background-base-surface-2))',
        border: `1px solid ${getBorderColor()}`,
        fontSize: theme.text.size.lg,
        fontWeight: theme.fontWeights.medium,
        borderRadius: 4,
        padding: '0 15px',
        '&:hover': {
          borderColor: 'rgb(var(--input-stroke-hover))',
          backgroundColor: 'rgb(var(--_background-base-surface-2))',
          borderWidth: '1px',
        },
        '&:active': {
          borderColor: 'rgb(var(--input-stroke-focus))',
          backgroundColor: 'rgb(var(--_background-base-surface-2))',
        },
        '&:focus': {
          borderWidth: '2px',
          borderColor: 'rgb(var(--input-stroke-focus))',
          backgroundColor: 'rgb(var(--_background-base-surface-2))',
          outline: 'none',
        },
        '&:disabled': {
          borderColor: 'rgb(var(--input-stroke-disabled))',
          backgroundColor: 'rgb(var(--_background-base-surface-2))',
        },
      };
    },
  };

  const mergedStyles = {
    ...defaultStyles,
    ...(variant === 'blue' ? blueVariantStyles : {}),
    ...(variant === 'surface-2' ? transparentVariantStyles : {}),
    ...(variant === 'ia' ? IAStyles : {}),
    ...overrideStyles,
  };

  const selectId = `react-select-${label}`;

  return (
    <SelectWrapper className={clsx(className, error && 'error')}>
      {label && (
        <Label className={clsx(subtitle ? '!mb-0.5' : '')} htmlFor={selectId}>
          {label}
        </Label>
      )}
      {subtitle && (
        <span className="mb-2 text-sm text-content-base-subdued">
          {subtitle}
        </span>
      )}
      {rightContent && (
        <span className="absolute right-1 !mb-4 ml-2">{rightContent}</span>
      )}
      <Select
        {...props}
        menuPlacement={props.menuPlacement ?? 'auto'}
        isClearable={isClearable}
        styles={mergedStyles}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: CustomDropdownIndicator,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          Option: (props) => <CustomOption {...props} variant={variant} />,
          MultiValueRemove: CustomMultiValueRemove,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          MultiValue: CustomMultiValue,
          Placeholder: CustomPlaceholder,
          ...components,
        }}
        classNames={{
          menu: (state) =>
            (variant !== 'ia' &&
              (state.placement === 'top'
                ? 'rounded-tl-md rounded-tr-md border border-input-stroke-filled border-b-0'
                : 'rounded-bl-md rounded-br-md border border-input-stroke-filled border-t-0')) ||
            '',
          menuList: () =>
            props.menuPlacement === 'top'
              ? 'rounded-tl-[5px] rounded-tr-[5px]'
              : 'rounded-bl-[5px] rounded-br-[5px]',
          control: (state) =>
            state.isDisabled ? '!bg-input-background-disabled' : '',
          ...classNames,
        }}
        unstyled
        hideSelectedOptions={false}
        inputId={selectId}
        blurInputOnSelect={!props.isMulti}
        closeMenuOnSelect={!props.isMulti}
        inputValue={props.inputValue ?? inputValue}
        onInputChange={
          props.onInputChange
            ? props.onInputChange
            : (value, { action }) => {
                if (action === 'set-value' && props.isMulti) setInputValue('');
                if (['input-change', 'menu-close'].includes(action)) {
                  setInputValue(value);
                }
              }
        }
      />
      {info && (
        <InputDetail className="text-content-base-default">{info}</InputDetail>
      )}
      <InfoComponent selectedValue={props.value} />
      <ErrorText className="text-input-content-error">{error}</ErrorText>
    </SelectWrapper>
  );
};
